import * as APIRoutes from '@mm/api-routes';
import {
  LCAttributeFilter,
  LCConversationSubset,
} from '../actions/LiveChatCRUDActions';
import {
  LiveChatChannel,
  LivechatConversationModel,
  LiveChatInboxStatus,
} from '../model/LivechatConversationModel';
import { LivechatConversationState } from '../model/LivechatConversationStateModel';
import AuthenticatedController from './AuthenticatedController';
import { DEFAULT_HEADERS } from './Controller';

export type SendMessageReturnType = {
  success: boolean;
  messageId?: string;
};

export type UpdateConversationStatusConversationReturnType = {
  createdAt: string;
  id: number;
  muted: boolean;
  mutedSecondsRemaining: number;
  mutedAt: string | null;
  mutedTill: string | null;
};

export type LiveChatNodeHeadersParams = {
  userId: number;
  userAuthToken: string;
  remoteFbPageId: string;
};

export type LiveChatSearch = {
  language: string;
  query: string;
};

export type GetFilteredLCConversationsResult = {
  conversations: LivechatConversationModel[];
};

export type GetPaginatedLCConversationSessionMessages = {
  remoteUserId: string;
  selectedSessionId: number;
};

export type FilteredConversationServiceParams = {
  conversationSubset?: LCConversationSubset;
  conversationState: LivechatConversationState | 'all';
  assignedUserId?: number | 'unassigned' | 'all';
  selectedChannels?: LiveChatChannel[];
  selectedTags?: string[];
  selectedAttribute?: LCAttributeFilter | null;
};

export type UpdateConversationStatus = {
  remotePageId: string;
  mute?: boolean;
  muteFor?: number; // Seconds
  inboxStatus?: LiveChatInboxStatus;
  assignedUserId?: string;
  remoteUserIds: string[] | 'all';
  filterParams?: FilteredConversationServiceParams;
};
type UpdateConversationStatusReturnType = {
  success: boolean;
  conversationsUpdated: number;
};

export type DeleteConversations = {
  remotePageId: string;
  remoteUserIds: string[] | 'all';
  filterParams?: FilteredConversationServiceParams;
};

export type GetFilteredConversationsParams =
  FilteredConversationServiceParams & {
    remotePageId: string;
    remoteUserIds?: string[];
    page: number;
    perPage: number;
  };

type DeleteConversationsReturnType = {
  success: boolean;
  contactsDeleted: number;
};
export type GetPaginatedLCMessages = {
  pageState: string | null;
  remoteUserId: string | undefined;
};

export type UpdateSelectedLCRemoteUserId = {
  remoteUserId: string | undefined;
  sessionSuffix: string;
};

export const getHeaders = ({
  userId,
  userAuthToken,
  remoteFbPageId,
}: LiveChatNodeHeadersParams) => ({
  ...DEFAULT_HEADERS,
  'MM-User-Id': userId.toString(),
  'MM-User-Authtoken': userAuthToken,
  'MM-Remote-Page-Id': remoteFbPageId.toString(),
});

export default class LiveChatController extends AuthenticatedController {
  static sendMessage(
    remotePageId: string,
    remoteUserId: string,
    message: string,
    localPersonaId?: number,
    pageId?: number,
    likeHeart?: boolean,
    clientMessageId?: string,
  ): Promise<SendMessageReturnType> {
    return super.post(APIRoutes.LIVE_CHAT_SEND_MESSAGE, {
      source: 'inbox',
      remotePageId,
      remoteUserId,
      message,
      localPersonaId,
      pageId,
      likeHeart,
      clientMessageId,
    });
  }

  static sendAsset(
    remotePageId: string,
    remoteUserId: string,
    assetId: number,
    localPersonaId?: number,
    clientMessageId?: string,
  ): Promise<SendMessageReturnType> {
    return super.post(APIRoutes.LIVE_CHAT_SEND_MESSAGE, {
      source: 'inbox',
      remotePageId,
      remoteUserId,
      assetId,
      localPersonaId,
      clientMessageId,
    });
  }

  static getFilteredConversations(
    params: GetFilteredConversationsParams,
  ): Promise<GetFilteredLCConversationsResult> {
    return super.post(APIRoutes.LIVE_CHAT_FILTERED_CONVERSATIONS, params);
  }

  static getFilteredContactsCount(
    params: GetFilteredConversationsParams,
  ): Promise<{ contactsCount: number }> {
    return super.post(APIRoutes.LIVE_CHAT_FILTERED_CONTACTS_COUNT, params);
  }

  static getConversationSessionMessages = async (
    params: LiveChatNodeHeadersParams &
      GetPaginatedLCConversationSessionMessages,
  ) => {
    const {
      userId,
      userAuthToken,
      remoteFbPageId,
      remoteUserId,
      selectedSessionId,
    } = params;

    const headers = getHeaders({ userId, userAuthToken, remoteFbPageId });

    return fetch(
      `${APIRoutes.LC_CONVERSATION_SESSION_MESSAGES}/${selectedSessionId}?remoteUserId=${remoteUserId}`,
      {
        method: 'GET',
        headers,
      },
    );
  };

  static getPaginatedMessages = async (
    params: LiveChatNodeHeadersParams & GetPaginatedLCMessages,
  ) => {
    const { userId, userAuthToken, remoteFbPageId, remoteUserId, pageState } =
      params;

    const headers = getHeaders({ userId, userAuthToken, remoteFbPageId });

    return fetch(`${APIRoutes.LC_MESSAGES}/${remoteUserId}/${pageState}`, {
      method: 'GET',
      headers,
    });
  };

  static batchGetPaginatedMessages = async (
    params: LiveChatNodeHeadersParams & {
      conversations: GetPaginatedLCMessages[];
    },
  ) => {
    const { userId, userAuthToken, remoteFbPageId, conversations } = params;

    const headers = getHeaders({ userId, userAuthToken, remoteFbPageId });

    return fetch(`${APIRoutes.BATCH_LC_MESSAGES}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(conversations),
    });
  };

  static updateSelectedRemoteUserId = async (
    params: LiveChatNodeHeadersParams & UpdateSelectedLCRemoteUserId,
  ) => {
    const {
      userId,
      userAuthToken,
      remoteFbPageId,
      remoteUserId,
      sessionSuffix,
    } = params;

    const headers = getHeaders({ userId, userAuthToken, remoteFbPageId });

    const url = `${APIRoutes.LC_SELECTED_REMOTE_USER}/${remoteUserId}?sessionSuffix=${sessionSuffix}`;

    return fetch(url, {
      method: 'GET',
      headers,
    });
  };

  static updateConversationStatus(
    params: UpdateConversationStatus,
  ): Promise<UpdateConversationStatusReturnType> {
    return super.put(APIRoutes.LIVE_CHAT_CONVERSATION_STATUS, params);
  }

  static updateSingleConversationStatus(
    params: UpdateConversationStatus,
  ): Promise<UpdateConversationStatusReturnType> {
    return super.post(APIRoutes.LIVE_CHAT_CONVERSATION_STATUS, params);
  }

  static deleteConversations(
    params: DeleteConversations,
  ): Promise<DeleteConversationsReturnType> {
    return super.deleteWithJsonResult(
      APIRoutes.LIVE_CHAT_FILTERED_CONVERSATIONS,
      undefined,
      undefined,
      params,
    );
  }

  static searchContacts = async (
    params: LiveChatNodeHeadersParams & LiveChatSearch,
  ) => {
    const { userId, userAuthToken, remoteFbPageId, language, query } = params;

    const headers = getHeaders({ userId, userAuthToken, remoteFbPageId });

    return fetch(
      `${APIRoutes.LC_CONTACTS_SEARCH}?query=${encodeURIComponent(
        query,
      )}&language=${language}`,
      {
        method: 'GET',
        headers,
      },
    );
  };

  static searchConversations = async (
    params: LiveChatNodeHeadersParams & LiveChatSearch,
  ) => {
    const { userId, userAuthToken, remoteFbPageId, language, query } = params;

    const headers = getHeaders({ userId, userAuthToken, remoteFbPageId });

    return fetch(
      `${APIRoutes.LC_CONVERSATIONS_SEARCH}?query=${encodeURIComponent(
        query,
      )}&language=${language}`,
      {
        method: 'GET',
        headers,
      },
    );
  };
}
